import { useEffect, useMemo, useState } from "react";

import { useLivePrice } from "@/hooks/useLivePrice";
import { Bell, ChevronDown, Info } from "lucide-react";
import {
  FUNDING_EXCHANGE_NAMES,
  formatFundingCountdown,
  formatFundingRate,
  fundingRateClass,
  fundingVenues,
} from "@/lib/funding";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import {
  getPriceMarkets,
  getPriceServiceRest,
  peekPriceMarkets,
  type FuturesServiceExchange,
  toServiceSymbol,
  type ServiceMarket,
} from "@/lib/prices";

/** "binance_futures" -> "binance", for the logo. */
const venueLogoCode = (venue: string) => venue.replace(/_futures$/, "");

/** How often the other venues' rates (shown in the picker) are re-read. */
const OTHER_RATES_EVERY_MS = 60_000;

interface AlertFundingProps {
  symbol: string;
  /** The user's connected exchange codes, in autotrade rank order. */
  preferred: string[];
  enabled: boolean;
}

/**
 * Funding in alert details (redesign layout): the rate and next payment on
 * the left, a picker of the exchanges that list the token on the right.
 * Starts on the user's first connected exchange that lists it.
 */
export function AlertFunding({ symbol, preferred, enabled }: AlertFundingProps) {
  const [markets, setMarkets] = useState<ServiceMarket[]>(
    () => (symbol ? peekPriceMarkets(symbol) : null) ?? []
  );
  const [chosen, setChosen] = useState<FuturesServiceExchange | null>(null);

  useEffect(() => {
    setChosen(null);
    if (!enabled || !symbol) return;
    let cancelled = false;
    getPriceMarkets(symbol)
      .then((found) => {
        if (!cancelled) setMarkets(found);
      })
      .catch(() => {
        // No venues: nothing to switch between.
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, enabled]);

  const venues = useMemo(
    () => fundingVenues(markets, preferred),
    [markets, preferred]
  );
  const selected = chosen && venues.includes(chosen) ? chosen : venues[0] ?? null;

  // Every venue's rate in one /v1/prices request, so each button shows
  // whether funding is positive or negative there.
  const [rates, setRates] = useState<Partial<Record<string, number>>>({});
  const venueKey = venues.join(",");
  useEffect(() => {
    setRates({});
    if (!enabled || !symbol || venues.length < 2) return;
    let cancelled = false;
    const load = () => {
      getPriceServiceRest()
        .getPrices(
          venues.map((exchange) => ({ exchange, symbol: toServiceSymbol(symbol) }))
        )
        .then((rows) => {
          if (cancelled) return;
          const next: Partial<Record<string, number>> = {};
          for (const row of rows) {
            if (row.funding) next[row.exchange] = row.funding.rate;
          }
          setRates(next);
        })
        .catch(() => {
          // Buttons stay uncoloured.
        });
    };
    load();
    const timer = setInterval(load, OTHER_RATES_EVERY_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [venueKey, symbol, enabled]);

  const { price } = useLivePrice(
    symbol,
    selected ?? undefined,
    true,
    enabled && !!selected
  );
  const funding = price?.exchange === selected ? price.funding : null;

  if (!selected) return null;
  const now = Date.now();
  const next =
    funding?.nextFundingTime && funding.nextFundingTime > now
      ? formatFundingCountdown(funding.nextFundingTime, now)
      : null;
  return (
    <div className="flex items-center gap-4">
      {/* Rate on the venue in view */}
      <div className="flex min-w-0 flex-1 items-start gap-1.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#525252]">
          <Bell className="size-4 text-white" />
        </span>
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-xs leading-[18px] text-[#a3a3a3]">
            Funding Rate
            <span title="Paid between longs and shorts every interval. Positive: longs pay shorts. Negative: shorts pay longs.">
              <Info className="size-3" aria-label="What is funding?" />
            </span>
          </p>
          {funding ? (
            <p className="text-sm font-semibold leading-5">
              <span className={fundingRateClass(funding.rate)}>{formatFundingRate(funding.rate)}</span>
              {funding.intervalHours ? (
                <span className="font-medium text-[#a3a3a3]"> / {funding.intervalHours}h</span>
              ) : null}
            </p>
          ) : (
            <p className="text-sm font-semibold leading-5 text-white">---</p>
          )}
          {next && <p className="text-[10px] font-semibold leading-3 text-[#a3a3a3]">Next in {next}</p>}
        </div>
      </div>

      <div className="w-px self-stretch bg-white/10" aria-hidden />

      {/* Exchange picker */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-xs leading-[18px] text-[#a3a3a3]">Exchange (Funding Rate)</p>
        <label
          className={`relative flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-[rgba(12,24,33,0.7)] px-2 backdrop-blur-[4px] ${
            venues.length > 1 ? "cursor-pointer" : ""
          }`}
        >
          <ExchangeLogo code={venueLogoCode(selected)} size={16} />
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-white">
            {FUNDING_EXCHANGE_NAMES[selected]}
          </span>
          {venues.length > 1 && <ChevronDown className="size-4 shrink-0 text-[#a3a3a3]" />}
          {venues.length > 1 && (
            <select
              aria-label="Exchange for funding rate"
              value={selected}
              onChange={(e) => setChosen(e.target.value as FuturesServiceExchange)}
              className="absolute inset-0 cursor-pointer opacity-0"
            >
              {venues.map((venue) => {
                const rate = venue === selected ? funding?.rate ?? rates[venue] : rates[venue];
                return (
                  <option key={venue} value={venue}>
                    {FUNDING_EXCHANGE_NAMES[venue]}
                    {rate !== undefined ? ` (${formatFundingRate(rate)})` : ""}
                  </option>
                );
              })}
            </select>
          )}
        </label>
      </div>
    </div>
  );
}
